import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  format,
  isSameYear,
  isToday,
  isTomorrow,
  isYesterday,
  startOfDay
} from "date-fns";
import { intl } from "../i18n/TypedIntl";

export enum Mode {
  SHORT = "short",
  LONG = "long"
}

export const getRelativeDateString = (
  timestamp: number,
  options: { relativeOnlyToday?: boolean; mode?: Mode; } = { mode: Mode.LONG }
): string => {
  const { relativeOnlyToday, mode } = options;
  const now = Date.now();

  if (isToday(timestamp)) {
    if (differenceInSeconds(now, timestamp) < 5) {
      return intl.formatMessage(mode === "short" ? "time.now.short" : "time.now.long");
    } else if (differenceInMinutes(now, timestamp) < 1) {
      return (
        5 * Math.floor(differenceInSeconds(now, timestamp) / 5) + intl.formatMessage("time.seconds")
      );
    } else if (differenceInHours(now, timestamp) < 1) {
      return differenceInMinutes(now, timestamp) + intl.formatMessage("time.ago.minutes");
    } else {
      return differenceInHours(now, timestamp) + intl.formatMessage("time.ago.hours");
    }
  } else if (relativeOnlyToday) {
    return getTimeString(timestamp);
  } else if (isYesterday(timestamp) && mode === "long") {
    return intl.formatMessage("date.yesterday");
  } else {
    const days = differenceInDays(startOfDay(now), startOfDay(timestamp));
    if (days <= 30) {
      return days + intl.formatMessage("time.ago.days");
    } else {
      return getFriendlyDateString(timestamp, { mode });
    }
  }
};

export const getFuzzyTime = (timestamp: number): string => {
  const now = Date.now();
  const differenceInSec = differenceInSeconds(now, timestamp);
  if (differenceInSec < 5) {
    return intl.formatMessage("time.now.long");
  } else if (differenceInSec < 60) {
    return (
      5 * Math.floor(differenceInSec / 5) + intl.formatMessage("time.ago.seconds")
    );
  }
  else {
    return getTimeString(timestamp);
  }
};

export const getTimeString = (timestamp: number): string => format(timestamp, "H:mm");

export const getFriendlyDateString = (
  timestamp: number,
  options: { useToday?: boolean; useTomorrow?: boolean; mode?: Mode; useWeekday?: boolean; } = {
    mode: Mode.LONG,
    useWeekday: false
  }
): string => {
  if (options.useToday && isToday(timestamp)) {
    return intl.formatMessage("date.today").toLowerCase();
  } else if (options.useTomorrow && isTomorrow(timestamp)) {
    return intl.formatMessage("date.tomorrow").toLowerCase();
  }
  const sameYear = isSameYear(timestamp, Date.now());
  const formatOptions = {
    day: "numeric",
    month: options.mode,
    weekday: sameYear && options.useWeekday ? options.mode : undefined,
    year: sameYear ? undefined : "numeric"
  } as const;
  return intl.formatDate(timestamp, formatOptions);
};

export const getDateSeparatorString = (timestamp: number): string => {
  if (isToday(timestamp)) {
    return intl.formatMessage("date.today");
  } else if (isYesterday(timestamp)) {
    return intl.formatMessage("date.yesterday");
  }
  return intl.formatDate(timestamp, {
    weekday: "long",
    year: "numeric",
    day: "numeric",
    month: "short"
  });
};

export const getTimeZoneString = (): string | undefined => {
  // it returns date and time zone separated with a comma
  const dateAndTimeZone = new Intl.DateTimeFormat([], { timeZoneName: "short" }).format(Date.now());
  const parts = dateAndTimeZone.split(",");
  return (parts && parts.length > 1 && parts[1]!.trim()) || undefined;
};

export const getMidnightTimestamp = (timestamp: number) => {
  return timestamp - (timestamp % (24 * 60 * 60 * 1000));
};

export const formatNumberWithLeadingZero = (n: number): string =>
  n >= 10 ? n.toString() : "0" + n;

export const getNumericFormatDate = (date: Date, delimiter: string): string => {
  const day = formatNumberWithLeadingZero(date.getDate());
  const month = formatNumberWithLeadingZero(date.getMonth() + 1);
  const year = date.getFullYear();
  const hour = formatNumberWithLeadingZero(date.getHours());
  const minutes = formatNumberWithLeadingZero(date.getMinutes());

  return `${day}.${month}.${year} ${delimiter} ${hour}:${minutes}`;
};

export const getNumericFormatTimestamp = (timestamp: number, delimiter: string): string => {
  return getNumericFormatDate(new Date(timestamp), delimiter);
};
